/**
 * "No work order yet" + "Use it as the work order" on the job page (2026-10-21).
 *
 * Cintas job #102678734: the vendor work order was uploaded here as a plain
 * document, so it never reached WO Intake and the tech had nothing to fill or
 * sign. Job #092678170 had no Cintas work order at all. Two pieces:
 *
 *   · a red banner on a work-order customer's job that has no work order,
 *     with one tap to upload it (the file goes through WO Intake for THIS job);
 *   · the offer after an upload that looks like a work order — one tap, with
 *     Undo. Never re-filed without the tap.
 *
 * The server does the work (POST /attachments/:id/use-as-original-work-order):
 * the same reader, template match and link as WO Intake's "Link to job".
 */
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";

export interface WorkOrderOffer {
    attachmentId: string;
    reason: string;
    workOrderNumber: string | null;
}

interface UseResult {
    itemId: string;
    jobId: string;
    status: string;
    workOrderNumber: string | null;
}

const POLL_MS = 3_000;
const POLL_MAX_MS = 150_000;

function messageOf(err: unknown, fallback: string): string {
    const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
    return body?.message ?? fallback;
}

/** The one tap, its progress, and its Undo. */
export function useUseAsOriginalWorkOrder(jobId: string) {
    const qc = useQueryClient();
    const [pending, setPending] = useState(false);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["job", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-queue"] });
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
    };

    const undo = async (itemId: string) => {
        try {
            await api.post(`/api/attachments/original-work-order/${itemId}/undo`, {});
            refresh();
            toast.success("Undone. The file is a plain document again.");
        } catch (err) {
            toast.error(messageOf(err, "Couldn't undo that."));
        }
    };

    const done = (r: { itemId: string; workOrderNumber: string | null }) => {
        refresh();
        toast.success(
            r.workOrderNumber
                ? `Work order ${r.workOrderNumber} is on this job now. The tech can fill and sign it.`
                : "The work order is on this job now. The tech can fill and sign it.",
            { duration: 12_000, action: { label: "Undo", onClick: () => void undo(r.itemId) } },
        );
    };

    /** The worker reads it; watch until it lands (or give an honest answer). */
    const watch = async (itemId: string, toastId: string | number) => {
        const started = Date.now();
        while (Date.now() - started < POLL_MAX_MS) {
            await new Promise((r) => setTimeout(r, POLL_MS));
            try {
                const s = await api.get<{ status: string; workOrderNumber: string | null }>(
                    `/api/attachments/original-work-order/${itemId}`,
                );
                if (s.status === "processing") continue;
                toast.dismiss(toastId);
                if (s.status === "linked") {
                    done({ itemId, workOrderNumber: s.workOrderNumber });
                } else {
                    refresh();
                    toast.warning("We couldn't put it on this job by ourselves. It's waiting in Work Order Intake.", {
                        duration: 12_000,
                        action: {
                            label: "Open it",
                            onClick: () => window.location.assign(`/jobs/work-orders/review/${itemId}`),
                        },
                    });
                }
                return;
            } catch {
                // A blip — keep watching.
            }
        }
        toast.dismiss(toastId);
        toast.message("Still reading the work order. It will show on this job when it's done.");
    };

    const start = async (attachmentId: string) => {
        setPending(true);
        try {
            const r = await api.post<UseResult>(`/api/attachments/${attachmentId}/use-as-original-work-order`, {});
            if (r.status === "linked") {
                done(r);
            } else if (r.status === "processing") {
                const id = toast.loading("Reading the work order…");
                void watch(r.itemId, id);
            } else {
                refresh();
                toast.warning("It's in Work Order Intake now. Finish it there.", { duration: 10_000 });
            }
        } catch (err) {
            toast.error(messageOf(err, "Couldn't use that file as the work order."));
        } finally {
            setPending(false);
        }
    };

    /** After an upload: ask, in plain words, with one tap. */
    const offer = (o: WorkOrderOffer, fileName: string) => {
        toast(`${fileName} looks like a work order. Use it as the work order?`, {
            description: o.reason,
            duration: 30_000,
            action: { label: "Use as work order", onClick: () => void start(o.attachmentId) },
        });
    };

    return { start, offer, pending };
}

/** Red banner: this job needs a work order and has none. */
export function NoWorkOrderBanner({
    jobId,
    isOffice,
    candidate,
}: {
    jobId: string;
    isOffice: boolean;
    /** A plain document already on the job whose name looks like the work order. */
    candidate?: { id: string; file_name: string } | null;
}) {
    const qc = useQueryClient();
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const use = useUseAsOriginalWorkOrder(jobId);

    const upload = async (file: File | undefined) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("jobId", jobId);
            fd.append("category", "document");
            const r = await api.postForm<{ attachment?: { id: string } | null; filedAsReport?: unknown }>(
                "/api/attachments/upload",
                fd,
            );
            qc.invalidateQueries({ queryKey: ["job", jobId, "documents"] });
            if (r?.filedAsReport || !r?.attachment?.id) {
                toast.warning("That file read as a test report, so it was filed as one. Upload the work order PDF.");
                return;
            }
            await use.start(r.attachment.id);
        } catch (err) {
            toast.error(messageOf(err, "Upload failed."));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const busy = uploading || use.pending;
    return (
        <div
            role="alert"
            data-testid="no-work-order-banner"
            className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 text-red-950 dark:border-red-500/50 dark:bg-red-500/10 dark:text-red-100"
        >
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold">No work order yet</p>
                <p className="text-xs text-red-900/80 dark:text-red-100/80">
                    This customer sends work orders. The tech has nothing to fill or sign until it&apos;s on this job.
                </p>
                {isOffice && candidate && (
                    <p className="mt-1 text-xs font-semibold" data-testid="no-work-order-candidate">
                        {candidate.file_name} is already on this job and looks like it.
                    </p>
                )}
            </div>
            {isOffice && candidate && (
                <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 bg-background"
                    disabled={busy}
                    onClick={() => void use.start(candidate.id)}
                >
                    Use {candidate.file_name.length > 28 ? "that file" : candidate.file_name}
                </Button>
            )}
            {isOffice && (
                <>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        data-testid="no-work-order-upload-input"
                        onChange={(e) => void upload(e.target.files?.[0])}
                    />
                    <Button
                        size="sm"
                        className="bg-red-600 text-white hover:bg-red-700"
                        disabled={busy}
                        onClick={() => inputRef.current?.click()}
                    >
                        {busy ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                            <Upload className="mr-1.5 h-4 w-4" />
                        )}
                        Upload work order
                    </Button>
                </>
            )}
        </div>
    );
}
